import React, { useMemo } from "react"
import { MenuItem } from "@mui/material"
import { useFormikContext } from "formik"
import { DateTime } from "luxon"
import { useQuery } from "@apollo/client/react"
import { TextField } from "@/components/form"
import { createHoursList } from "@/shared/datetime"
import { GetOpeningHoursDocument } from "@/graphql/graphql"
import { getOpeningHoursForDate } from "@/utils/opening_hours"
import i18n from "@/i18n"

interface TimeRangeSelectorProps {
  venue: { slug: string }
  date: DateTime | null
  startTimeFieldName?: string
  endTimeFieldName?: string
}

const TimeRangeSelector: React.FC<TimeRangeSelectorProps> = ({
  venue,
  date,
  startTimeFieldName = "startTime",
  endTimeFieldName = "endTime",
}) => {
  const { values } = useFormikContext<Record<string, string | null | undefined>>()

  const { data: openingHoursData } = useQuery(GetOpeningHoursDocument, {
    variables: { slug: venue.slug },
  })

  const { startHour, endHour } = useMemo(
    () => getOpeningHoursForDate(openingHoursData?.venue?.openingHours, date),
    [openingHoursData?.venue?.openingHours, date],
  )

  const timeOptions = useMemo(() => {
    if (!date) return []
    return createHoursList(date, startHour, endHour)
  }, [date, startHour, endHour])

  const startTimeValue = values[startTimeFieldName]
  const endTimeOptions = useMemo(() => {
    if (!startTimeValue || typeof startTimeValue !== "string") return timeOptions
    const startDateTime = DateTime.fromISO(startTimeValue)
    if (!startDateTime.isValid) return timeOptions
    return timeOptions.filter(([, optionDate]) => optionDate > startDateTime)
  }, [timeOptions, startTimeValue])

  return (
    <div className="grid grid-cols-2 gap-3" data-testid="panel-reservations-time-range">
      <TextField name={startTimeFieldName} label={i18n.t("app.reservation.labels.start_time")} select disabled={!date}>
        {timeOptions.map(([time, optionDate]) => (
          <MenuItem key={time} value={optionDate.toISO() ?? ""}>
            {time}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        name={endTimeFieldName}
        label={i18n.t("app.reservation.labels.end_time")}
        select
        disabled={!date || !startTimeValue}
      >
        {endTimeOptions.map(([time, optionDate]) => (
          <MenuItem key={time} value={optionDate.toISO() ?? ""}>
            {time}
          </MenuItem>
        ))}
      </TextField>
    </div>
  )
}

export default TimeRangeSelector
